import { useState, useRef, useEffect, Fragment } from 'react'
import {
  Box,
  Input,
  List,
  ListItem,
  InputGroup,
  InputRightElement,
  useOutsideClick,
  Text,
  Tag,
  TagLabel,
} from '@chakra-ui/react'

import { Close } from 'components/icons'
import CustomFormFieldErrorMessage from 'components/tools/CustomForm/commonComponents/CustomFormFieldErrorMessage'
import { CustomFormViewerQuestionProps } from 'components/tools/CustomForm/types/CustomFormViewerQuestionProps'
import { useCustomFormViewer } from 'components/tools/CustomForm/components/Viewer/contexts/CustomFormViewerManager/Provider'
import validateSingleSelectionListOnCustomForm from 'components/tools/CustomForm/components/Viewer/utils/validateSingleSelectionListOnCustomForm'
import useCustomFormGetDefaultValue from '../../../../hooks/useCustomFormGetDefaultValue'
import useCustomFormHandleErrorWithScrollBehavior from '../../../../hooks/useCustomFormHandleErrorWithScrollBehavior'

export default function CustomFormAutoComplete({
  question,
  questionKey,
  formMethods,
  disabled,
}: CustomFormViewerQuestionProps) {
  const [query, setQuery] = useState('')
  const [isOpen, setIsOpen] = useState(false)

  const { removeErrorFromQuestion, setErrorOnQuestion, thisQuestionHasError } = useCustomFormViewer()

  const { defaultValue } = useCustomFormGetDefaultValue({ question, questionKey, disabled })

  const questionWithError = thisQuestionHasError(question.slug)?.question

  const boxRef = useRef<HTMLDivElement | null>(null)

  const selectedValue = formMethods?.watch ? formMethods?.watch(questionKey) : ''

  const options = question?.options || []

  useOutsideClick({
    ref: boxRef,
    handler: () => setIsOpen(false),
  })

  const handleChangeQueryAndValue = (newValue: string) => {
    const errorMessage = validateSingleSelectionListOnCustomForm({ text: newValue, isRequired: question.is_required })
    if (errorMessage === '') {
      if (formMethods?.setValue) {
        formMethods?.setValue(questionKey, newValue)
      }
      removeErrorFromQuestion(question.slug)
      setQuery(newValue)
    } else {
      setErrorOnQuestion(question, errorMessage)
    }
  }

  const handleChangeInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value
    setQuery(value)
    setIsOpen(true)
  }

  const handleFilteredOptions = () => {
    if (query.trim().length > 0) {
      const filteredOptions = options.filter((item) => item.toLowerCase().includes(query.toLowerCase()))
      return filteredOptions
    }
    return options
  }

  const handleFocusInput = () => {
    setIsOpen(true)
  }

  const handleResetValue = () => {
    const errorMessage = validateSingleSelectionListOnCustomForm({ text: '', isRequired: question.is_required })
    if (errorMessage === '') {
      formMethods?.setValue(questionKey, '')
    } else {
      setErrorOnQuestion(question, errorMessage)
    }
    setQuery('')
  }

  const handleOnBlurInput = () => {
    // if (selectedValue) {
    //   setQuery(selectedValue)
    // }
  }

  const handleSelect = (value: string) => {
    handleChangeQueryAndValue(value)
    setIsOpen(false)
  }

  useEffect(() => {
    if (formMethods?.setValue && query === '') {
      formMethods?.setValue(questionKey, '', { shouldValidate: true })
    }
  }, [query])

  useEffect(() => {
    if (question.answer) {
      const answer = question.answer[0]
      handleChangeQueryAndValue(answer)
    }
  }, [question])

  useEffect(() => {
    if (defaultValue) {
      handleChangeQueryAndValue(defaultValue)
    }
  }, [defaultValue])

  useCustomFormHandleErrorWithScrollBehavior({ questionWithError, questionSlug: question.slug })

  return (
    <Box ref={boxRef} minWidth="100%" maxW="400px" position="relative">
      <InputGroup>
        <Input
          autoComplete="off"
          isDisabled={disabled}
          value={query}
          onChange={handleChangeInput}
          onBlur={handleOnBlurInput}
          onFocus={handleFocusInput}
          placeholder={question.placeholder}
        />
        <InputRightElement width="max-content" pe={2}>
          {selectedValue && (
            <Close className="accessible" _hover={{ scale: '1.3', color: 'danger' }} onClick={handleResetValue} />
          )}
        </InputRightElement>
      </InputGroup>

      {selectedValue && (
        <Tag mt={2}>
          <TagLabel>{selectedValue}</TagLabel>
        </Tag>
      )}

      {isOpen && (
        <List
          zIndex={1}
          bg="bgContent"
          position="absolute"
          overflowY="auto"
          width="100%"
          maxHeight="300px"
          borderWidth="1px"
          borderColor="color"
          rounded="md"
          mt={2}
          p={1}
        >
          {handleFilteredOptions().length > 0 ? (
            <Fragment>
              {handleFilteredOptions().map((item) => (
                <ListItem
                  key={item}
                  px={4}
                  py={2}
                  className="accessible"
                  _hover={{ bg: 'primary' }}
                  onClick={() => handleSelect(item)}
                >
                  {item}
                </ListItem>
              ))}
            </Fragment>
          ) : (
            <Box p={2}>
              <Text color="primary" fontSize="md">
                Nenhum item encontrado
              </Text>
            </Box>
          )}
        </List>
      )}

      <Box mt={2}>
        <CustomFormFieldErrorMessage message={questionWithError?.error_message} />
      </Box>
    </Box>
  )
}
